import { useEffect, useState, useRef } from 'react'
import Link from 'next/link'
import { createClient } from '@sanity/client'
import { toHTML, uriLooksSafe } from '@portabletext/to-html'
import Head from 'next/head'
import Script from 'next/script'
import { useRouter } from 'next/router'
import {
  useWindowSize,
  Footer,
  Header,
  TryEdworking,
  Subheader,
  HubspotChat,
  EdworkingPromotion,
  SimilarArticles,
  TopBanner,
} from '../../../js/components'
import { timeDiff, newLive } from '../../../js'
import {
  OrganizationSchema,
  WebPageSchema,
  ArticleSchema,
  VideoSchema,
  BreadcrumbListSchema,
  FAQSchema,
} from '../../../js/schemas'
import Image from 'next/image'
import path from 'path'
import fs from 'fs/promises' // or 'fs' if using callbacks
import { fetchWithRetry } from 'js/components'

const myPortableTextComponents = {
  types: {
    image: ({ value }) => {
      return `<img src="${value.url}" alt="${value.alt || ''}" />`
    },
  },
  marks: {
    link: ({ children, value }) => {
      const href = value.href || ''

      if (uriLooksSafe(href)) {
        const rel = href.startsWith('/') ? undefined : 'noreferrer noopener nofollow'
        return `<a href="${href}" rel="${rel}">${children}</a>`
      }

      // If the URI appears unsafe, render the children (eg, text) without the link
      return children
    },
  },
}

export const client = createClient({
  projectId: 'k9mx6lbx', //process.env.SANITY_PROJECT_ID,
  dataset: 'production', //process.env.SANITY_DATASET,
  useCdn: true,
  apiVersion: '2024-01-18', // use current date (YYYY-MM-DD) to target the latest API version
  // token: process.env.SANITY_SECRET_TOKEN // Only if you want to update content with the client
})

export async function getStaticPaths() {
  const data = await fetchWithRetry(process.env.NEXT_PUBLIC_VENUSAUR, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operationName: 'GetBlogsApproved',
      query:
        'query GetBlogsApproved{getBlogsApproved{ id language title category updatedAt }}',
    }),
  })

  const blogs = data?.data?.getBlogsApproved.filter((b) => b.category !== 'guides')
  const allblogs = blogs

  const queryPosts = `
    *[_type == "post" && approved == true && category->title != "guides"]{
      "title": slug.current,
      language,
      "category": category->title,
      publishedAt
    }`
  const sanityBlogs = await client.fetch(queryPosts)
  const array = allblogs.concat(sanityBlogs).map((blog) => {
    return [
      {
        locale: blog.language,
        params: { category: blog.category, blog: blog.title },
      },
    ]
  })
  return {
    paths: array.flat(1),
    fallback: 'blocking',
  }
}

export async function getStaticProps({ res, params, locale, locales }) {
  const article = params.blog,
    category = params.category

  if (article === 'best-workload-management-tools-in-2023') {
    return {
      redirect: {
        destination: `/blog/${category}/best-workload-management-tools`,
        statusCode: 301,
      },
    }
  } else if (article === 'how-to-create-an-effective-document-management-wo') {
    return {
      redirect: {
        destination: `/blog/remotework/how-to-create-an-effective-document-management-workflow`,
        statusCode: 301,
      },
    }
  } else if (article === '5-best-jira-alternatives-to-try') {
    return {
      redirect: {
        destination: `/blog/${category}/5-best-jira-alternatives-to-try-in-2022`,
        statusCode: 301,
      },
    }
  } else if (article === 'best-meeting-management-software') {
    return {
      redirect: {
        destination: `/blog/${category}/best-meeting-management-software-in-2022`,
        statusCode: 301,
      },
    }
  } else if (article === 'como-ser-mas-productivo-5-habitos-que-deberias-ad') {
    return {
      redirect: {
        destination: `/es/blog/remotework/como-ser-mas-productivo-5-habitos-que-deberias-adquirir`,
        statusCode: 301,
      },
    }
  } else if (article === 'fluxograma-de-processo-o-que-e-como-fazer-e-exemp') {
    return {
      redirect: {
        destination: `/pt/blog/startups/fluxograma-de-processo-o-que-e-como-fazer-e-exemplos`,
        statusCode: 301,
      },
    }
  } else if (article === 'datenbasierte-geschaftsentscheidungen-was-es-ist-') {
    return {
      redirect: {
        destination: `/de/blog/remotework/datenbasierte-geschaftsentscheidungen-was-es-ist-und-wie-es-funktioniert`,
        statusCode: 301,
      },
    }
  } else if (article === 'wrike-vs-jira-the-complete-comparison') {
    return {
      redirect: {
        destination: `/blog/remotework/wrike-vs-jira-the-complete-comparison-in-2023`,
        statusCode: 301,
      },
    }
  } else if (article === 'matriz-de-decisao-o-que-e-e-como-usar') {
    return {
      redirect: {
        destination: `/pt/blog/startups/matriz-de-decisao-o-que-e-como-usar`,
        statusCode: 301,
      },
    }
  } else if (article === 'metodologias-de-gestao-de-projetos-5-modelos-mais') {
    return {
      redirect: {
        destination: `/pt/blog/remotework/metodologias-de-gestao-de-projetos-5-modelos-mais-populares`,
        statusCode: 301,
      },
    }
  } else if (article === 'si-continua-ad-aggiornarlo-in-base-allattivita-sv') {
    return {
      redirect: {
        destination: `/it/blog/remotework/si-continua-ad-aggiornarlo-in-base-allattivita-svolta`,
        statusCode: 301,
      },
    }
  } else if (article === 'top-5-techniques-de-memorisation-pour-tes-revisio') {
    return {
      redirect: {
        destination: `/fr/blog/remotework/top-5-techniques-de-memorisation-pour-tes-revisions`,
        statusCode: 301,
      },
    }
  } else if (article === 'list-of-top-10-miro-alternatives') {
    return {
      redirect: {
        destination: `/blog/startups/list-of-top-10-miro-alternatives-in-2022`,
        statusCode: 301,
      },
    }
  } else if (article === 'la-methode-eeisenhower-productivite-et-definition') {
    return {
      redirect: {
        destination: `/fr/blog/remotework/la-methode-eisenhower-productivite-et-definition`,
        statusCode: 301,
      },
    }
  } else if (article === 'struttura-a-matrice-organizzazione-e-come-funzio') {
    return {
      redirect: {
        destination: `/it/blog/startups/struttura-a-matrice-organizzazione-e-come-funziona`,
        statusCode: 301,
      },
    }
  } else if (article === 'struttura-a-matrice-oraganizzazione-e-come-funzio') {
    return {
      redirect: {
        destination: `/it/blog/startups/struttura-a-matrice-organizzazione-e-come-funziona`,
        statusCode: 301,
      },
    }
  } else if (article === 'il-project-manager-cosa-fa-ruolo-e-cemptenze') {
    return {
      redirect: {
        destination: `/it/blog/remotework/il-project-manager-cosa-fa-ruolo-e-competenze`,
        statusCode: 301,
      },
    }
  } else if (article === 'top-5-google-keep-alternatives-in-2023') {
    return {
      redirect: {
        destination: `/blog/startups/top-5-google-keep-alternatives`,
        statusCode: 301,
      },
    }
  } else if (article === 'top-3-best-loom-alternatives-in-2022-edworking') {
    return {
      redirect: {
        destination: `/blog/${category}/top-3-best-loom-alternatives`,
        statusCode: 301,
      },
    }
  } else if (article === 'how-to-effectively-assign-tasks-to-team-members-t') {
    return {
      redirect: {
        destination: `/blog/${category}/how-to-effectively-assign-tasks-to-team-members`,
        statusCode: 301,
      },
    }
  } else if (article === '5-remote-work-software-features-you-need-for-stre') {
    return {
      redirect: {
        destination: `/blog/${category}/5-remote-work-software-features-streamline`,
        statusCode: 301,
      },
    }
  } else if (article === 'top-10-google-sheets-alternatives-in-2023') {
    return {
      redirect: {
        destination: `/blog/${category}/top-10-google-sheets-alternatives`,
        statusCode: 301,
      },
    }
  } else if (article === 'reviews-of-top-5-confluence-alternatives-in-2023') {
    return {
      redirect: {
        destination: `/blog/${category}/top-5-confluence-alternatives`,
        statusCode: 301,
      },
    }
  }
  const data1 = await fetchWithRetry(process.env.NEXT_PUBLIC_VENUSAUR, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operationName: 'GetBlog',
      query:
        'query GetBlog($blog: String!) {getBlog(blog: $blog){id title sumup keywords category rawTitle language article cover metaTitle metaDescription color url author { id firstname lastname description profileImg linkedin } createdAt updatedAt}}',
      variables: { blog: article },
    }),
  })
  const data2 = await fetchWithRetry(process.env.NEXT_PUBLIC_VENUSAUR, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      operationName: 'GetBlogsCategory',
      query:
        'query GetBlogsCategory($blog: String, $category: String, $language: String) {getBlogsCategory(blog: $blog, category: $category, language: $language){id rawTitle color language title category cover resume author { firstname lastname } createdAt}}',
      variables: { category, language: locale },
    }),
  })

  const queryPost = `
    *[_type == "post" && slug.current == $blog][0]{
      "id": _id,
      "title": slug.current,
      "sumup": body[0].children[0].text, // Assuming 'sumup' is derived from the body
      keywords,
      "category": category->title,
      "rawTitle": title,
      language,
      "article": body[]{
        ...,
        _type == 'image' => {
          "url": asset->url,
          alt,
        }
      },
      "cover": cover.asset->url, // Assuming cover image is stored in mainImage
      metaTitle,
      metaDescription,
      color,
      "url": slug.current,
      "author": author->{
        "id": _id,
        "firstname": name, // Assuming author's name is stored in a single field
        "lastname": "", // Separate field for last name if exists
        "description": bio[], // Retrieve bio as an array of blocks
        "profileImg": image.asset->url,
        linkedin
      },
      "createdAt": publishedAt,
      "updatedAt": _updatedAt
    }`
  const postData = await client.fetch(queryPost, { blog: article })
  if (postData) {
    postData.article = toHTML(postData.article, {
      components: myPortableTextComponents,
    })
    postData.author.description = toHTML(postData.author.description)
    postData.keywords = postData.keywords?.join(' ') ?? null
  }
  const blog = postData ?? data1?.data?.getBlog

  if ((!data1 && !postData) || (!postData && data1.errors)) {
    return {
      notFound: true,
    }
  }
  const similar = data2?.data?.getBlogsCategory
  if (category !== blog?.category || (blog.language && locale !== blog.language)) {
    return {
      redirect: {
        destination: `${blog.language === 'en' ? '' : '/' + blog.language}/blog/${
          blog.category
        }/${blog.title}`,
        statusCode: 301,
      },
    }
  }
  let content
  try {
    const filePath = path.join(process.cwd(), 'public', 'content', locale, 'blog.json')
    // Read the file
    const jsonData = await fs.readFile(filePath, 'utf8')
    content = JSON.parse(jsonData)
  } catch (error) {
    console.error('error', error)
  }
  return { props: { similar, blog, content, locale, locales, params } }
}
const ArticleProgress = () => {
  useEffect(() => {
    window.addEventListener('scroll', onScroll)
  }, [])
  const [scrollTop, setScrollTop] = useState(1)
  const onScroll = (e) => {
    const scroll1 = Math.round(
      ((e.target.documentElement.scrollTop + window.screen.height) /
        e.target.documentElement.scrollHeight) *
        100
    )
    setScrollTop(scroll1)
  }
  return (
    <div
      className="articleprogress"
      style={{
        background: `linear-gradient(90deg, #fcb50480   0%, #fcb50480   ${scrollTop}%, #09323a10 ${scrollTop}%, #09323a10 100%)`,
      }}
    ></div>
  )
}
const Share = ({ link, title }) => {
  const formattedTitle = encodeURIComponent(`Check out this article: ${title}`)
  return (
    <div className="sharing">
      <span>Share this post</span>
      <div className="icons">
        <Link
          target="_blank"
          rel="noreferrer"
          href={`https://twitter.com/intent/tweet?text=${formattedTitle}&url=${encodeURIComponent(
            link
          )}`}
        >
          <svg
            width="21.52"
            height="22"
            viewBox="0 0 1200 1227"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: '18px',
              height: '18px',
              display: 'block',
              fill: 'currentcolor',
              flexShrink: 0,
            }}
          >
            <path d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z" />
          </svg>
        </Link>
        <a
          target="_blank"
          rel="noreferrer"
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            link
          )}&quote=${formattedTitle}`}
        >
          <svg
            className="shareFacebook"
            viewBox="0 0 18 18"
            style={{
              width: '18px',
              height: '18px',
              display: 'block',
              fill: 'currentcolor',
              flexShrink: 0,
            }}
          >
            <path d="M17.999 8.999c0-4.97-4.029-8.999-8.999-8.999s-8.999 4.029-8.999 8.999c0 4.492 3.291 8.215 7.593 8.89v-6.289h-2.285v-2.601h2.285v-1.983c0-2.255 1.344-3.501 3.399-3.501 0.985 0 2.015 0.176 2.015 0.176v2.215h-1.135c-1.118 0-1.467 0.694-1.467 1.405v1.688h2.496l-0.399 2.601h-2.097v6.289c4.302-0.675 7.593-4.398 7.593-8.89z"></path>
          </svg>
        </a>
        <a
          target="_blank"
          rel="noreferrer"
          href={`https://t.me/share/url?url=${encodeURIComponent(
            link
          )}&text=${formattedTitle}`}
        >
          <svg
            className="shareTelegram"
            viewBox="0 0 24 24"
            style={{
              width: '20px',
              height: '20px',
              display: 'block',
              fill: 'currentcolor',
              flexShrink: 0,
            }}
          >
            <path d="M18.384,22.779c0.322,0.228 0.737,0.285 1.107,0.145c0.37,-0.141 0.642,-0.457 0.724,-0.84c0.869,-4.084 2.977,-14.421 3.768,-18.136c0.06,-0.28 -0.04,-0.571 -0.26,-0.758c-0.22,-0.187 -0.525,-0.241 -0.797,-0.14c-4.193,1.552 -17.106,6.397 -22.384,8.35c-0.335,0.124 -0.553,0.446 -0.542,0.799c0.012,0.354 0.25,0.661 0.593,0.764c2.367,0.708 5.474,1.693 5.474,1.693c0,0 1.452,4.385 2.209,6.615c0.095,0.28 0.314,0.5 0.603,0.576c0.288,0.075 0.596,-0.004 0.811,-0.207c1.216,-1.148 3.096,-2.923 3.096,-2.923c0,0 3.572,2.619 5.598,4.062Zm-11.01,-8.677l1.679,5.538l0.373,-3.507c0,0 6.487,-5.851 10.185,-9.186c0.108,-0.098 0.123,-0.262 0.033,-0.377c-0.089,-0.115 -0.253,-0.142 -0.376,-0.064c-4.286,2.737 -11.894,7.596 -11.894,7.596Z" />
          </svg>
        </a>
        <a
          target="_blank"
          rel="noreferrer"
          href={`https://wa.me/?text=${formattedTitle}%20${encodeURIComponent(link)}`}
        >
          <svg
            className="shareWhatsapp"
            viewBox="0 0 24 24"
            style={{
              width: '22px',
              height: '22px',
              display: 'block',
              fill: 'currentcolor',
              flexShrink: 0,
              marginTop: '-2px',
            }}
          >
            <path d="M20.52 3.482c-2.03-2.03-4.73-3.148-7.613-3.148-5.945 0-10.788 4.844-10.788 10.788 0 1.9.503 3.748 1.461 5.377l-1.555 5.667 5.821-1.528c1.576.893 3.356 1.367 5.06 1.367h.001c5.945 0 10.788-4.844 10.788-10.788 0-2.88-1.12-5.578-3.148-7.607zm-7.64 15.81h-.001c-1.57 0-3.221-.434-4.606-1.258l-.33-.196-3.453.906.924-3.319-.215-.339c-.907-1.431-1.388-3.09-1.388-4.81 0-4.868 3.961-8.829 8.829-8.829 2.357 0 4.572.918 6.242 2.589 1.67 1.67 2.588 3.885 2.588 6.242 0 4.868-3.961 8.829-8.828 8.829zm4.491-6.22c-.246-.123-1.458-.718-1.685-.799-.226-.08-.392-.122-.557.123-.164.246-.634.799-.779.964-.145.164-.287.185-.533.061-.246-.123-1.038-.383-1.979-1.223-.732-.654-1.226-1.461-1.37-1.707-.145-.246-.015-.38.109-.503.112-.111.246-.287.37-.43.121-.142.161-.242.242-.404.08-.164.04-.307-.02-.43-.061-.123-.536-1.295-.733-1.77-.194-.461-.394-.396-.537-.404-.142-.007-.307-.01-.47-.01-.163 0-.43.061-.654.307-.225.246-.864.846-.864 2.064 0 1.217.891 2.396 1.015 2.561.123.164 1.451 2.23 3.515 3.129.492.213.875.34 1.174.434.494.157.944.135 1.299.082.396-.061 1.224-.5 1.398-.984.17-.49.17-.909.12-.996-.05-.087-.185-.142-.388-.246z"></path>
          </svg>
        </a>
        <a
          target="_blank"
          rel="noreferrer"
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            link
          )}`}
        >
          <svg
            className="shareLinkedin"
            viewBox="0 0 22 18"
            style={{ width: '21px', display: 'block', fill: 'currentcolor', flexShrink: 0 }}
          >
            <path d="M4.98 3.5c0 1.38-1.12 2.5-2.5 2.5s-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5zm.02 3.5h-5v11h5v-11zm7.98 0h-4.8v11h4.8v-6.1c0-3.5 4.5-3.8 4.5 0v6.1h4.8v-6.8c0-5.64-6.5-5.44-9.3-2.7v-1.5z"></path>
          </svg>
        </a>
        <a
          target="_blank"
          rel="noreferrer"
          href={`mailto:?subject=${formattedTitle}&body=${formattedTitle}%20${encodeURIComponent(
            link
          )}`}
        >
          <svg
            className="shareEmail"
            viewBox="0 0 24 18"
            style={{
              width: '22px',
              height: '22px',
              display: 'block',
              fill: 'currentcolor',
              flexShrink: 0,
            }}
          >
            <path d="m12.0158 9.96411c.4532 0 .8643-.21201 1.3492-.67639l9.2121-8.782953c-.4216-.333146-1.1489-.504767-2.1713-.504767h-17.0224c-.917 0-1.58103.161526-1.97102.474481l9.24372 8.813239c.4849.46438.8959.67639 1.3597.67639zm-11.710135 6.25909 7.673255-7.29892-7.641634-7.22826c-.221344.35333-.337286.91867-.337286 1.71621v11.16547c0 .7672.105402 1.3124.305665 1.6455zm23.399235-.0101c.1897-.3432.2951-.8783.2951-1.6354v-11.16547c0-.77735-.1159-1.35278-.3373-1.69602l-7.6205 7.20807zm-20.1107 1.7869h17.0224c.9592 0 1.6443-.1817 2.0553-.525l-7.8102-7.4402-.6114.5754c-.7378.6865-1.444 1.0095-2.2345 1.0095-.801 0-1.5072-.323-2.24505-1.0095l-.61133-.5754-7.79974 7.4301c.44269.3534 1.19104.5351 2.23452.5351z"></path>
          </svg>
        </a>
      </div>
    </div>
  )
}

export default function Blog({ similar, blog, content, locale, params }) {
  const title = blog?.rawTitle ? blog?.rawTitle : blog?.title, //.replace(/\b(<![àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇßØøÅåÆæœ])([a-z])/g, l => l.toUpperCase())
    metaTitle = blog.metaTitle
      ? blog.metaTitle.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
      : blog?.rawTitle.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()),
    author = blog?.author?.firstname + ' ' + blog?.author?.lastname,
    created = timeDiff(blog?.createdAt),
    color = blog?.color,
    length = Math.ceil(blog?.article?.split(' ').length / 200)

  const [promotion, setPromotion] = useState(false)
  const [slideInBanner, setSlideInBanner] = useState(false)

  const { asPath } = useRouter()

  const onMouseOut = () => {
    const goon = localStorage.getItem('promotion')
    if (!goon) setPromotion(true)
    document.body.removeEventListener('mouseleave', onMouseOut)
  }

  useEffect(() => {
    setTimeout(function () {
      document.body.addEventListener('mouseleave', onMouseOut)
    }, 3000)
    if (relatedInfo !== null && relatedInfo?.slidein !== null) {
      setTimeout(() => setSlideInBanner(true), 5000)
    }
  }, [])

  const optimization = content?.optimization
  function getRelatedInfo(blogTitle) {
    blogTitle = blogTitle.toLowerCase()

    for (const category in optimization) {
      //if (optimization[category].blogs.some(blog => blog.toLowerCase() === blogTitle)) {

      const blogs = optimization[category].blogs.map((title) => title.toLowerCase())
      if (blogs.includes(blogTitle)) {
        return {
          intext: optimization[category].intext ?? null,
          slidein: optimization[category].slidein ?? null,
          button: optimization[category].button ?? null,
        }
      }
    }
    return null
  }
  const relatedInfo = getRelatedInfo(blog.title)
  console.log('relatedInfo', relatedInfo)
  const videoObject = blog?.category === 'podcast'
  const faqsSection =
    blog.article.indexOf('<h2>FAQs</h2>') >= 0
      ? blog.article.substring(blog.article.indexOf('<h2>FAQs</h2>') + 12 + 1)
      : null
  var h4Tags = new RegExp('<h4>(.*?)</h4>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var pTags = new RegExp('<p>(.*?)</p>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  const questions = faqsSection?.match(h4Tags)?.map((q) => q.substring(4, q.length - 5))
  const answers = faqsSection?.match(pTags)?.map((a) => a.substring(3, a.length - 4))
  const listfaqs = questions?.map((q, i) => {
    return { question: q, answer: answers[i] }
  })

  return (
    <>
      <Script
        src="https://www.googletagmanager.com/gtag/js?id=UA-166939348-2"
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            
            gtag('config', 'UA-166939348-2');
        `}
      </Script>
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-NRW96HD');
        `}
      </Script>
      <Script id="hotjar" strategy="afterInteractive">
        {`
          (function(h,o,t,j,a,r){
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:2080824,hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);
          })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
        `}
      </Script>
      <OrganizationSchema />
      <WebPageSchema name={metaTitle} description={blog.metaDescription} />
      {videoObject ? (
        <VideoSchema
          blog={blog}
          url={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}${asPath}`}
        />
      ) : (
        <ArticleSchema
          blog={blog}
          url={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}${asPath}`}
        />
      )}
      <BreadcrumbListSchema
        items={[
          {
            name: 'Home',
            url: `https://edworking.com/${locale !== 'en' ? `${locale}/` : ''}`,
          },
          {
            name: 'Blog',
            url: `https://edworking.com/${locale !== 'en' ? `${locale}/` : ''}blog`,
          },
          {
            name: 'Article',
            url: `https://edworking.com${locale !== 'en' ? `/${locale}` : ''}${asPath}`,
          },
        ]}
      />
      <FAQSchema faqs={listfaqs} />
      <Head>
        <title>{metaTitle}</title>

        <meta itemProp="name" content={metaTitle} />
        <meta itemProp="image" content={blog.cover} />
        <meta itemProp="description" content={blog.metaDescription} />

        <meta name="description" content={blog.metaDescription} />

        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={blog.metaDescription} />

        <meta property="og:image" content={blog.cover} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="3000" />
        <meta property="og:image:height" content="1500" />
        <meta
          property="og:image"
          content={blog.cover?.replace('dratini/blog/', 'dratini/blog/small/')}
        />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="300" />
        <meta property="og:image:height" content="150" />

        <meta property="og:image:secure_url" content={blog.cover} />
        <meta
          property="og:url"
          content={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}/blog/${
            params.category
          }/${params.blog}`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="en_GB" />

        <meta name="msapplication-TileImage" content="http://www.example.com/image01.jpg" />
        <meta
          property="og:site_name"
          content={`${title.replace(/\b\w/g, (l) => l.toUpperCase())}`}
        />
        <link rel="shortcut icon" href="/images/favicon.ico" />

        <meta
          property="og:url"
          content={`https://edworking.com${
            locale !== 'en' ? `/${locale}` : ''
          }/blog/${blog?.category?.toLowerCase()}/${blog?.title?.replace(/ /g, '-')}`}
        />
        {/*locales.map((locale1, index) => <link key={index} rel="alternate" hrefLang={locale1} href={`https://edworking.com${locale1 !== 'en' ? `/${locale1}` : ''}/campaign`} /> )*/}
        <link
          rel="alternate"
          hrefLang={locale}
          href={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}/blog/${
            params.category
          }/${params.blog}`}
        />
        <link
          rel="alternate"
          href={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}/blog/${
            params.category
          }/${params.blog}`}
          hrefLang="x-default"
        />
        <link
          rel="canonical"
          href={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}/blog/${
            params.category
          }/${params.blog}`}
          key="canonical"
        />
      </Head>
      <noscript
        dangerouslySetInnerHTML={{
          __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NRW96HD" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
        }}
      />
      <link itemProp="thumbnailUrl" href={blog.cover} />
      <span itemProp="thumbnail" itemScope itemType="http://schema.org/ImageObject">
        <link
          itemProp="url"
          href={`https://edworking.com/blog/${blog?.category?.toLowerCase()}/${blog?.title?.replace(
            / /g,
            '-'
          )}`}
        />
      </span>
      <EdworkingPromotion promotion={promotion} content={content.exitbanner} />
      {/*<TopBanner content={content.topbanner} />*/}
      <Header content={content.header} nolanguage topbanner={true} />
      {blog?.category === 'podcast' ? (
        <>
          <VideoBlog blog={blog} content={content.banner} />
          <Organization author={blog.author} />
        </>
      ) : (
        <>
          <ArticleProgress />
          <Subheader
            type={'blog'}
            title={title}
            author={author}
            created={created}
            length={length}
          />
          <Image
            priority
            width={1200}
            height={300}
            style={{
              width: '90%',
              objectFit: blog.color ? 'contain' : 'cover',
              background: blog.color ? blog.color : 'white',
            }}
            src={
              blog.cover
                ? blog.cover
                : 'https://storage.googleapis.com/dratini/EdworkingDefault.png'
            }
            className={`coverarticle${blog.cover ? '' : ' default'}`}
            alt="article cover"
          />
          <Article
            content={content.banner}
            blog={blog}
            relatedInfo={relatedInfo}
            params={params}
          />
          <Author
            content={content.authors.filter((a) => blog.author.id === a.id)}
            author={blog.author}
          />
        </>
      )}
      <SimilarArticles similar={similar} />
      <TryEdworking content={content.endbanner} />
      <Footer content={content.footer} />
      {slideInBanner ? (
        <SlideInBanner
          slideInBanner={slideInBanner}
          relatedInfo={relatedInfo}
          blog={blog}
          content={content.banner}
        />
      ) : !relatedInfo?.intext ? (
        <StickyBanner blog={blog} content={content.banner} />
      ) : null}
      <HubspotChat />
      <div id="livenoti" className="livenoti"></div>
    </>
  )
}
const Banner = ({ blog }) => {
  const cta = 'Get Started Now',
    headline = 'All your work in one place',
    subheadline = 'All-in-one platform for your team and your work. Register now for Free.'
  return (
    <section className="banner">
      <Image
        width="150"
        height="150"
        className="image"
        src="https://storage.googleapis.com/dratini/Edworking%20Logo.png"
        alt=""
      />
      <div className="text">
        <div className="headline">{blog.bannerheadline ? blog.bannerheadline : headline}</div>
        <div className="subheadline">
          {blog.bannersubheadline ? blog.bannersubheadline : subheadline}
        </div>
      </div>
      <a className="blueButton" href="https://app.edworking.com/signup/user/">
        {blog.bannercta ? blog.bannercta : cta}
      </a>
    </section>
  )
}
export const VideoBlog = ({ blog, content }) => {
  const [time, setTime] = useState(0)
  const [movedTime, setMovedTime] = useState(0)
  const [speakers, setSpeakers] = useState()
  const [transcript, setTranscript] = useState()
  const [speaker, setSpeaker] = useState()
  const [menu, setMenu] = useState('summaary')
  const size = useWindowSize()
  const articleRef = useRef()
  const refSpeakers = useRef()
  const refMovedTime = useRef()
  const refTime = useRef()
  const refVideo = useRef()
  const refTranscript = useRef()

  var timeTag = new RegExp('[[][0-9][0-9]:[0-9][0-9]:[0-9][0-9][.][0-9][0-9][0-9]]?', 'g')

  refSpeakers.current = speakers
  refMovedTime.current = movedTime
  refTime.current = time
  refTranscript.current = transcript
  useEffect(() => {
    const sec = () => {
      if (!refSpeakers.current) setTimeout(() => sec(), 1000)
      else {
        setTime(refVideo.current?.currentTime)
        const index =
          refSpeakers.current
            .map((speaker) => {
              return (
                parseInt(speaker.innerText.substring(1, 3)) * 60 * 60 +
                parseInt(speaker.innerText.substring(4, 6)) * 60 +
                parseFloat(speaker.innerText.substring(7, 13))
              )
            })
            .findIndex((s) => s > refVideo.current?.currentTime) - 1
        refTranscript.current.map(
          (t, i) => (t.style.color = index === i ? '#0ac9ef' : '#09323a')
        )
        setSpeaker(index)
        setMovedTime(refTime.current)
        const scroll = articleRef.current
        if (scroll)
          scroll.scrollTo({
            behavior: 'smooth',
            top: refSpeakers.current[index]?.offsetTop,
          })
        refMovedTime.current !== refTime.current && refSpeakers.current[index]?.scrollTo //scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
        setTimeout(() => sec(), 500)
      }
    }
    sec()
  }, [])

  useEffect(() => {
    if (!articleRef?.current) return
    const speak = Array.from(articleRef.current?.querySelectorAll('strong'))
    setTranscript(
      Array.from(articleRef.current?.querySelectorAll('p')).filter(
        (a) => a.childNodes[0]?.localName !== 'strong' && a.childNodes[0]?.localName !== 'br'
      )
    )
    setSpeakers(speak)
  }, [blog, size.width])

  if (size.width < 1000) {
    return (
      <section className="videoblog">
        <div className="videosection">
          <div className="videoleft">
            <VideoPlayer src={blog.url} refVideo={refVideo} />
            <div className="title">{blog.rawTitle}</div>
            <div className="selector">
              <span id="selector" className={`top ${menu}`}></span>
              <div id="monthly" className="tab selected" onClick={() => setMenu('summaary')}>
                <span>Summary</span>
              </div>
              <div id="yearly" className="tab" onClick={() => setMenu('transcript')}>
                <span>Transcription</span>
              </div>
            </div>
          </div>
        </div>
        <div className="summary">
          {menu === 'transcript' ? (
            <div
              ref={articleRef}
              className="transcription"
              dangerouslySetInnerHTML={{ __html: blog.article }}
            />
          ) : (
            <span className="resume">{blog.sumup}</span>
          )}
        </div>
        <Banner content={content} blog={blog} />
      </section>
    )
  }
  //.replace(timeTag, (time) => { const h = parseInt(time.substring(1, 3));const m = parseInt(time.substring(4, 6));const s = parseInt(time.substring(7, 11));return (h > 0 ? h + 'h ' : '') + m + 'm ' + s' })
  return (
    <section className="videoblog">
      <div className="videosection">
        <div className="videoleft">
          <VideoPlayer src={blog.url} refVideo={refVideo} />
          <div
            className="title"
            style={{
              maxWidth: refVideo.current?.offsetWidth ? refVideo.current?.offsetWidth : '100%',
            }}
          >
            {blog.rawTitle}
          </div>
        </div>
        <div
          ref={articleRef}
          className="transcription"
          dangerouslySetInnerHTML={{ __html: blog.article }}
        />
      </div>
      <div className="summary">
        <div className="title">Summary</div>
        <span className="resume">{blog.sumup}</span>
      </div>
      <Banner blog={blog} />
    </section>
  )
}
function pad(d) {
  return d < 10 ? '0' + d.toString() : d.toString()
}

const VideoPlayer = ({ src, refVideo }) => {
  const [volume, setVolume] = useState(0.8)
  const current = `${pad(
    ((Math.round(refVideo.current?.currentTime) % 3600) -
      (Math.round(refVideo.current?.currentTime) % 60)) /
      60
  )}:${pad(Math.round(refVideo.current?.currentTime) % 60)}`
  const duration = `${pad(
    ((Math.round(refVideo.current?.duration) % 3600) -
      (Math.round(refVideo.current?.duration) % 60)) /
      60
  )}:${pad(Math.round(refVideo.current?.duration) % 60)}`
  const refNavigation = useRef()
  const refBar = useRef()
  const refVolume = useRef()

  useEffect(() => {
    if (volume) refVideo.current.volume = volume
  }, [volume])

  return (
    <div className="videoPlayer">
      <video
        onClick={() =>
          refVideo.current?.paused ? refVideo.current.play() : refVideo.current.pause()
        }
        style={{ cursor: 'pointer' }}
        controls={false}
        ref={refVideo}
        src={src}
        alt="Cover"
      />
      {refVideo.current?.paused && (
        <Image
          className="play"
          onClick={() => refVideo.current.play()}
          width={80}
          height={66}
          src={'/images/Play2.svg'}
          alt="Pause"
        />
      )}
      <div className="controlsContainer">
        <div
          className="button"
          onClick={() =>
            refVideo.current?.paused ? refVideo.current.play() : refVideo.current.pause()
          }
        >
          <Image
            className="pause"
            width={9}
            height={10}
            src={refVideo.current?.paused ? '/images/Play.svg' : '/images/Pause.svg'}
            alt="Pause"
          />
        </div>
        <div className="navigation" ref={refNavigation}>
          <span>{current}</span>
          <div
            ref={refBar}
            className="progress"
            onClick={(e) =>
              (refVideo.current.currentTime =
                (refVideo.current?.duration *
                  Math.round(e.clientX - refBar.current.getBoundingClientRect().left)) /
                refBar.current.offsetWidth)
            }
            style={{
              background: `linear-gradient(90deg, #FFFFFFb3 0%, #FFFFFFb3 ${Math.round(
                (refVideo.current?.currentTime / refVideo.current?.duration) * 100
              )}%, #FFFFFF1a ${Math.round(
                (refVideo.current?.currentTime / refVideo.current?.duration) * 100
              )}%, #FFFFFF1a 100%)`,
            }}
          ></div>
          <span>{duration}</span>
          <div
            ref={refVolume}
            className="volume"
            onClick={(e) =>
              setVolume(
                (
                  (e.clientX - refVolume.current.getBoundingClientRect().left) /
                  refVolume.current.offsetWidth
                ).toFixed(2)
              )
            }
            style={{
              background: `linear-gradient(90deg, #FFFFFFb3 0%, #FFFFFFb3 ${
                volume * 100
              }%, #FFFFFF1a ${volume * 100}%, #FFFFFF1a 100%)`,
            }}
          ></div>
        </div>
        <div className="button">
          <Image
            className="expand"
            onClick={() => refVideo.current.requestFullscreen()}
            width={16}
            height={15}
            src="/images/Expand.svg"
            alt="Expand"
          />
        </div>
      </div>
    </div>
  )
}
export const Index = ({ articleRef, language, article, params, rawTitle }) => {
  const [clicked, setClicked] = useState('')
  const [scrolled, setScrolled] = useState('')
  const [titles, setTitles] = useState([])
  var content = Array.from(titles)
  var onlyText = new RegExp('[^a-zA-Z0-9-]', 'g')
  function onClick(c) {
    c.scrollIntoView({ block: 'start', behavior: 'smooth' })
    setClicked(c.innerText)
    setTimeout(() => {
      setClicked('')
    }, 1200)
  }

  useEffect(() => {
    setTitles(articleRef.current.querySelectorAll('h2'))
    window.location.hash?.substring(1) &&
      document.getElementById(window.location.hash.substring(1))?.scrollIntoView()
  }, [article, articleRef])

  window.onscroll = () => {
    content?.map((c) => {
      const offset = c.getBoundingClientRect().top // - c.offsetTop
      if (offset < 200) setScrolled(c.innerText)
      return offset
    })
  }
  const title = content?.map((c, index) => {
    const selected = clicked === c.innerText || (clicked === '' && scrolled === c.innerText)
    return (
      <li key={index} className="clickable" onClick={() => onClick(c)}>
        <a
          className={`element${selected ? ' selected' : ''}`}
          href={`#${c.innerText.replace(/ /g, '-').replace(onlyText, '').toLowerCase()}`}
          style={{
            color:
              clicked === c.innerText
                ? '#16ccf0'
                : clicked === '' && scrolled === c.innerText
                  ? '#16ccf0'
                  : '#09323A',
          }}
        >
          {c.innerText}
        </a>
      </li>
    )
  })
  return (
    <div className="content" style={{ marginTop: 10 }}>
      <div className="extra-header">
        <Link href="/blog" className="title back-button">
          <Image
            src="/images/arrow-left.png"
            alt="Arrow to all blogs"
            width={10}
            height={10}
            marginRigh={10}
          />
          {language === 'es'
            ? 'Todas las publicaciones'
            : language === 'it'
              ? 'Tutti i post'
              : language === 'fr'
                ? 'Tous les articles'
                : language === 'pt'
                  ? 'Todas as postagens'
                  : language === 'de'
                    ? 'Alle Beiträge'
                    : 'All posts'}
        </Link>
      </div>
      <ol className="elements">{title}</ol>
      <div className="content">
        <Share
          link={`https://edworking.com/${language === 'en' ? '' : `${language}/`}${
            params.category !== 'guides' ? `blog/` : ''
          }${params.category}/${params.blog}`}
          title={rawTitle}
        />
      </div>
    </div>
  )
}
const Author = ({ author, content }) => {
  const [linkedin, setLinkdin] = useState(false)
  return (
    <section className="resumewriter">
      <Image
        className="profile"
        width={200}
        height={200}
        src={author.profileImg}
        alt="article cover"
      />
      <div className="body">
        <span className="name">
          About the Author: {author.firstname} {author.lastname}{' '}
          {author.linkedin && (
            <a href={author.linkedin} target="_blank" rel="noreferrer">
              <Image
                width={15}
                height={15}
                onMouseOver={() => setLinkdin(true)}
                onMouseLeave={() => setLinkdin(false)}
                className={linkedin ? 'dark' : 'light'}
                src={linkedin ? '/images/inw.png' : '/images/inb.png'}
                alt="Linkedin"
              />
            </a>
          )}
        </span>
        <span className="description">
          {content.length ? (
            content[0].description
          ) : author.description?.startsWith('<p>') ? (
            <div dangerouslySetInnerHTML={{ __html: author.description }} />
          ) : (
            author.description
          )}
        </span>
      </div>
    </section>
  )
}
const Organization = ({ author }) => {
  const [linkedin, setLinkdin] = useState(false)
  return (
    <section className="resumewriter">
      <Image
        className="profile"
        width={200}
        height={200}
        src={author.profileImg}
        alt="article cover"
      />
      <div className="body">
        <span className="name">
          About the Organizer: {author.firstname} {author.lastname}{' '}
          {author.linkedin && (
            <a href={author.linkedin} target="_blank" rel="noreferrer">
              <Image
                width={15}
                height={15}
                onMouseOver={() => setLinkdin(true)}
                onMouseLeave={() => setLinkdin(false)}
                className={linkedin ? 'dark' : 'light'}
                src={linkedin ? '/images/inw.png' : '/images/inb.png'}
                alt="Linkedin"
              />
            </a>
          )}
        </span>
        <span className="description">{author.description}</span>
      </div>
    </section>
  )
}
export const Article = ({ relatedInfo, blog, content, params }) => {
  const edBanner = `
    <section class="banner">
      <Image class="image" src="https://storage.googleapis.com/dratini/Edworking%20Logo.png" alt="" />
      <div class="text">
        <div class="headline">${
          relatedInfo?.intext?.headline ?? blog.bannerheadline ?? content.headline
        }</div>
        <div class="subheadline">${
          relatedInfo?.intext?.subheadline ?? blog.bannersubheadline ?? content.subheadline
        }</div>
      </div>
      <a class="blueButton" href=${
        relatedInfo?.intext?.url1 ?? 'https://app.edworking.com/signup/user/'
      }>
        ${relatedInfo?.intext?.cta1 ?? blog.bannercta ?? content.cta}
      </a>
    </section>
  `

  var re = new RegExp(' ' + blog.keywords?.split(' ').join(' | ') + ' ', 'gi')
  var aTag = new RegExp('<a[^>]*>', 'gi')
  var hTag = new RegExp('<h[2-4]', 'g')
  var onlyText = new RegExp('[^a-zA-Z0-9-]', 'g')
  const spaceText = new RegExp('/ /g', 'g')
  const article = blog.article
  var blogEdworking = new RegExp('blog.edworking.com', 'g')
  var linkEdworking = new RegExp('href="www.edworking.com"', 'g')
  var httpEdworking = new RegExp('http://', 'g')
  const initBold = [...article?.matchAll(re)]
  const step = Math.ceil(initBold.length / 5)
  const bolds = initBold.reduce((bs, b, index) => {
    return index % step === 0 ? bs.concat(b.index) : bs
  }, [])
  const button1 = article.indexOf('<h', Math.round(article.length / 3))
  const button2 = article.indexOf('<h', Math.round((2 * article.length) / 3))
  const banner = article.indexOf('<h', Math.round(article.length / 2))

  const toolSectionRegex = /(<h2[^>]*?>)(\d+\.\s)(.*?)(<\/h2>)([\s\S]*?)(?=<h2[^>]*?>|$)/g
  const aiToolsArticle = article.replace(
    toolSectionRegex,
    (match, openTag, numbering, toolName, closeTag, content) => {
      const linkMatch = content.match(/<a[^>]+href=["']([^"']+)["'][^>]*>/i)
      const href = linkMatch ? linkMatch[1] : '#'
      const aiToolButton = `<a class="blueButton" style="text-align: center;" href=${href}>${relatedInfo?.button?.cta} ${toolName}</a>`
      return `${openTag}${numbering}${toolName}${closeTag}${content}${aiToolButton}`
    }
  )

  var alltags = new RegExp('<h2(.*?)>(.*?)</h2>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var faqs = new RegExp('<h2>FAQs</h2>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var allfaqs = new RegExp('<h2>FAQs</h2>?(.*)', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var h4Tags = new RegExp('<h4>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var pTags = new RegExp('<p>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  const faqsExist = article.match(faqs)
  var spantags = new RegExp(
    '<[/span][span][/strong][strong][/em][em][/u][u][/a][a](.*?)>',
    'g'
  ) //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var h2tags = new RegExp('<[/h2][h2](.*?)>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  const [width, setWidth] = useState()
  const articleRef = useRef()
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWidth(window?.innerWidth)
      window.newLive = newLive
    }
  }, [])
  return (
    <section ref={articleRef} className="blogarticle">
      {width && width > 500 && (
        <Index
          article={article}
          articleRef={articleRef}
          language={blog.language}
          params={params}
          rawTitle={blog.rawTitle}
        />
      )}
      <div className="article blog">
        <div
          className="body"
          dangerouslySetInnerHTML={{
            __html:
              (relatedInfo?.button ? aiToolsArticle : article)
                .replace(faqs, (matched) => {
                  return matched + '<div class="faqsbox">'
                })
                .replace(allfaqs, (matched) => {
                  return matched.replace(h4Tags, (mat) => {
                    return `<h4 onclick="let yo = this;do{yo = yo?.nextSibling;if(!yo){return}; yo?.classList.toggle('show');}while(yo?.nextSibling?.tagName !== 'H4');">`
                  })
                })
                .replace(blogEdworking, () => {
                  return 'edworking.com/blog'
                })
                .replace(linkEdworking, () => {
                  return 'href="https://edworking.com"'
                })
                .replace(httpEdworking, () => {
                  return 'https://'
                })
                ?.replace(hTag, (matched, index, a, b) => {
                  if (index === button1 || index === button2) return edBanner + matched
                  else return matched
                })
                .replace(re, (matched, index, a, b) => {
                  return bolds.includes(index) ? `<b>${matched}</b>` : matched
                })
                .replace(aTag, (matched, index, a, b) => {
                  return matched.includes('edworking.com')
                    ? matched
                    : matched.replace('noopener noreferrer', 'noopener noreferrer nofollow')
                })
                .replace(alltags, (match) => {
                  return (
                    `<h2 id="${match
                      .replace(spantags, '')
                      .replace(h2tags, '')
                      .split(' ')
                      .join('-')
                      .replace(onlyText, '')
                      .toLowerCase()}"><img onclick="navigator.clipboard.writeText('https://edworking.com/${
                      blog.language === 'en' ? '' : `${blog.language}/`
                    }blog/${blog.category}/${blog.title}#${match
                      .replace(spantags, '')
                      .replace(h2tags, '')
                      .split(' ')
                      .join('-')
                      .replace(onlyText, '')
                      .toLowerCase()}');window.newLive('/images/Logo.svg', 'Url ', 'copied to clipboard')" class="link" src="/images/link.png" />` +
                    match.replace(spantags, '').replace(h2tags, '') +
                    '</h2>'
                  )
                }) + (faqsExist ? '</div>' : ''),
          }}
        />
      </div>
    </section>
  )
}
const StickyBanner = ({ blog, content }) => {
  const [close, setClose] = useState(false)
  const cta = 'Get Started Now',
    headline = 'All your work in one place',
    subheadline = 'All-in-one platform for your team and your work. Register now for Free.'
  return (
    <section className="banner sticky" style={{ display: close ? 'none' : 'flex' }}>
      <div className="close" onClick={() => setClose(true)}>
        <Image width={22} height={22} src="/images/ClosePromotion.png" alt="Close popup" />
      </div>
      <Image
        className="image"
        width={150}
        height={150}
        src="https://storage.googleapis.com/dratini/Edworking%20Logo.png"
        alt=""
      />
      <div className="text">
        <div className="headline">
          {blog.bannerheadline ? blog.bannerheadline : content.headline}
        </div>
        <div className="subheadline">
          {blog.bannersubheadline ? blog.bannersubheadline : content.subheadline}
        </div>
      </div>
      <a className="blueButton" href="https://app.edworking.com/signup/user/">
        {blog.bannercta ? blog.bannercta : content.cta}
      </a>
    </section>
  )
}

const SlideInBanner = ({ relatedInfo, blog, content, slideInBanner }) => {
  const [close, setClose] = useState(false)

  return (
    <section
      className={`banner sticky slide${slideInBanner ? ' up' : ''}`}
      style={{ display: close ? 'none' : 'flex' }}
    >
      <div className="close" onClick={() => setClose(true)}>
        <Image width={22} height={22} src="/images/ClosePromotion.png" alt="Close popup" />
      </div>
      <Image
        className="image"
        width={150}
        height={150}
        src="https://storage.googleapis.com/dratini/Edworking%20Logo.png"
        alt=""
      />
      <div className="text">
        <div className="headline">{relatedInfo?.slidein.headline}</div>
        <div className="subheadline">{relatedInfo?.slidein.subheadline}</div>
      </div>
      <a className="blueButton" href={`${relatedInfo?.slidein.url2}`}>
        {relatedInfo?.slidein.cta2}
      </a>
    </section>
  )
}
